import type { OnboardingStep } from "../../Hooks/Dashboard/useHome";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";

/**
 * Lecture des étapes de démarrage (`useHome().getOnboardingSteps`) pour la
 * page « Démarrage » repensée (maquette 11a) : mêmes quatre étapes, mêmes
 * règles de complétion et de verrouillage, mais présentées dans l'ordre où on
 * les fait (profil, IBAN, premier élève, premier cours), avec un texte qui dit
 * pourquoi chacune compte et une seule action mise en avant. Les mots qui
 * changent d'une marque à l'autre (élève ou client, cours ou prestation, le nom
 * de la marque, l'avance immédiate absente chez Verified Trainer) passent par
 * `vocab`, `isVerifiedTrainer` et le `brandName` reçu.
 */

/** Ordre d'affichage, par identifiant d'étape du hook. */
export const START_ORDER = ["complete-profile", "iban", "student", "course"] as const;

export interface StartChecklistItem {
    id: string;
    title: string;
    hint: string | null;
    done: boolean;
}

export interface StartAction {
    label: string;
    run: () => void;
}

export interface StartStep {
    id: string;
    /** Numéro affiché, de 1 à 4. */
    number: number;
    title: string;
    description: string;
    /** Indication à droite du titre : « 4 choses à faire », « 2 minutes », « Terminé »… */
    hint: string;
    completed: boolean;
    locked: boolean;
    /** Étape mise en avant : la première qui reste à faire et qui n'est pas verrouillée. */
    current: boolean;
    checklist: StartChecklistItem[];
    /** Action principale, absente quand on attend quelqu'un d'autre ou que l'étape est verrouillée. */
    action: StartAction | null;
    /** Action discrète (« Voir mes élèves ») proposée en plus ou à la place de l'action principale. */
    secondary: StartAction | null;
    /** Texte affiché quand l'étape attend une autre personne (validation du compte, inscription de l'élève). */
    waiting: string | null;
}

export interface StartSummary {
    steps: StartStep[];
    total: number;
    completedCount: number;
    /** Numéro de l'étape en cours (1 à 4), ou null quand tout est terminé. */
    currentNumber: number | null;
}

const byId = (steps: OnboardingStep[]): Map<string, OnboardingStep> => new Map(steps.map((s) => [s.id, s]));

const childHint = (id: string, brandName: string): string | null => {
    switch (id) {
        case "complete-profile-choice": return "Auto-entrepreneur, société ou autre";
        case "cooperative": return "Adhésion, règlement intérieur et paiement";
        case "procuration-sap": return `Pour que ${brandName} déclare à votre place`;
        case "procuration-ae-sap": return "Statut d'auto-entrepreneur et déclaration SAP";
        case "declaration-nova": return "Votre déclaration de services à la personne";
        case "procuration-choice": return "Seulement si quelqu'un déclare à votre place";
        case "sign-conditions": return "Une signature, 2 minutes";
        case "valid-compte": return `Faite par ${brandName} sous 48 h après les trois premières`;
        case "student-add": return "Son nom et son e-mail suffisent";
        case "student-validate": return isVerifiedTrainer() ? "Il crée son compte de son côté" : "Il crée son compte et active son avance immédiate";
        default: return null;
    }
};

const childTitle = (child: OnboardingStep): string => {
    switch (child.id) {
        case "complete-profile-choice": return "Statut juridique";
        case "cooperative": return "Coopérative";
        case "procuration-sap": return "Procuration SAP";
        case "procuration-ae-sap": return "Procuration AE et SAP";
        case "declaration-nova": return "Déclaration Nova";
        case "procuration-choice": return "Procuration";
        case "sign-conditions": return "Conditions de service";
        case "valid-compte": return "Validation du compte";
        case "student-add": return `Invitation ${vocab.ofTheClient()}`;
        case "student-validate": return "Inscription validée";
        default: return child.title;
    }
};

const childActionLabel = (id: string): string => {
    switch (id) {
        case "complete-profile-choice": return "Commencer par le statut juridique";
        case "cooperative": return "Poursuivre l'adhésion à la coopérative";
        case "procuration-sap":
        case "procuration-ae-sap": return "Finaliser la procuration";
        case "declaration-nova": return "Finaliser la déclaration Nova";
        case "procuration-choice": return "Choisir ma procuration";
        case "sign-conditions": return "Signer les conditions de service";
        case "student-add": return `Inviter un ${vocab.client()}`;
        default: return "Continuer";
    }
};

const checklistOf = (step: OnboardingStep | undefined, brandName: string): StartChecklistItem[] =>
    (step?.children ?? []).map((child) => ({ id: child.id, title: childTitle(child), hint: childHint(child.id, brandName), done: child.completed }));

/** Première sous-étape qui reste à faire et qu'on peut lancer. */
const nextChild = (step: OnboardingStep | undefined): OnboardingStep | null =>
    (step?.children ?? []).find((child) => !child.completed && !child.locked) ?? null;

const remainingLabel = (step: OnboardingStep | undefined): string => {
    const total = step?.subSteps?.total ?? 1;
    const done = step?.subSteps?.completed ?? 0;
    const left = Math.max(0, total - done);
    return left === 1 ? "1 chose à faire" : `${left} choses à faire`;
};

const profileStep = (step: OnboardingStep | undefined, brandName: string): Omit<StartStep, "number" | "current"> => {
    const completed = !!step?.completed;
    const next = nextChild(step);
    const waitingValidation = !completed && !!step && !next && (step.children ?? []).some((c) => c.id === "valid-compte" && !c.completed);
    return {
        id: "complete-profile",
        title: "Complétez votre profil",
        description: `C'est ce qui permet à ${brandName} de valider votre compte. Sans validation, vous ne pouvez pas déclarer.`,
        hint: completed ? "Terminé" : waitingValidation ? "En cours de validation" : remainingLabel(step),
        completed,
        locked: false,
        checklist: checklistOf(step, brandName),
        action: !completed && next && next.action ? { label: childActionLabel(next.id), run: next.action } : null,
        secondary: null,
        waiting: waitingValidation
            ? `${brandName} vérifie votre dossier, en général sous 48 h. Vous recevrez un e-mail dès que votre compte sera validé.`
            : null,
    };
};

const ibanStep = (step: OnboardingStep | undefined, brandName: string): Omit<StartStep, "number" | "current"> => {
    const completed = !!step?.completed;
    return {
        id: "iban",
        title: "Ajoutez votre IBAN",
        description: `C'est sur ce compte que ${brandName} vous verse vos ${vocab.courses()}. Il est chiffré et ne sert qu'à ça.`,
        hint: completed ? "Terminé" : "2 minutes",
        completed,
        locked: false,
        checklist: [],
        action: !completed && step?.action ? { label: "Ajouter mon IBAN", run: step.action } : null,
        secondary: null,
        waiting: null,
    };
};

const studentStep = (step: OnboardingStep | undefined): Omit<StartStep, "number" | "current"> => {
    const completed = !!step?.completed;
    const children = step?.children ?? [];
    const add = children.find((c) => c.id === "student-add");
    const validate = children.find((c) => c.id === "student-validate");
    const invited = !!add?.completed;
    const waitingStudent = !completed && invited;
    const client = vocab.client();
    return {
        id: "student",
        title: `Ajoutez votre premier ${client}`,
        description: isVerifiedTrainer()
            ? `Vous l'invitez, il crée son compte de son côté. Comptez quelques jours pour cette partie.`
            : `Vous l'invitez, il crée son compte et active son avance immédiate de son côté. Comptez quelques jours pour cette partie.`,
        hint: completed ? "Terminé" : waitingStudent ? `En attente ${vocab.ofTheClient()}` : `Dépend ${vocab.ofTheClient()}`,
        completed,
        locked: false,
        checklist: checklistOf(step, ""),
        action: !completed && !invited && add?.action ? { label: childActionLabel("student-add"), run: add.action } : null,
        secondary: waitingStudent && validate?.action ? { label: `Voir mes ${vocab.clients()}`, run: validate.action } : null,
        waiting: waitingStudent
            ? `Votre ${client} n'a pas encore terminé son inscription. Relancez-le si besoin, vous pourrez déclarer dès qu'il aura validé.`
            : null,
    };
};

const courseStep = (step: OnboardingStep | undefined, brandName: string): Omit<StartStep, "number" | "current"> => {
    const completed = !!step?.completed;
    const locked = !!step?.locked && !completed;
    return {
        id: "course",
        title: `Déclarez ${vocab.yourFirstCourse()}`,
        description: `Quatre écrans, deux minutes. Votre ${vocab.client()} valide, ${brandName} vous verse.`,
        hint: completed ? "Terminé" : locked ? "Après les trois autres" : "2 minutes",
        completed,
        locked,
        checklist: [],
        action: !completed && !locked && step?.action ? { label: `Déclarer ${vocab.myFirstCourse()}`, run: step.action } : null,
        secondary: null,
        waiting: null,
    };
};

/** Construit la liste affichée à partir des étapes du hook. */
export const buildStartSummary = (steps: OnboardingStep[], brandName: string): StartSummary => {
    const map = byId(steps);
    const drafts = [
        profileStep(map.get("complete-profile"), brandName),
        ibanStep(map.get("iban"), brandName),
        studentStep(map.get("student")),
        courseStep(map.get("course"), brandName),
    ];
    const currentIndex = drafts.findIndex((s) => !s.completed && !s.locked);
    const fallbackIndex = drafts.findIndex((s) => !s.completed);
    const index = currentIndex >= 0 ? currentIndex : fallbackIndex;
    const result: StartStep[] = drafts.map((s, i) => ({ ...s, number: i + 1, current: i === index }));
    const completedCount = result.filter((s) => s.completed).length;
    return { steps: result, total: result.length, completedCount, currentNumber: index >= 0 ? index + 1 : null };
};

/** « Étape 1 sur 4, aucune étape terminée pour l'instant », « Étape 3 sur 4, 2 étapes terminées ». */
export const startProgressLabel = (summary: StartSummary): { step: string; detail: string } => {
    const step = summary.currentNumber ? `Étape ${summary.currentNumber} sur ${summary.total}` : "Toutes les étapes sont terminées";
    const detail = summary.completedCount === 0 ? "aucune étape terminée pour l'instant" : plural(summary.completedCount, "étape terminée", "étapes terminées");
    return { step, detail };
};
